import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  deletePayrollDocumentExternalMappings,
  getPayrollDocumentExternalApprovalIds,
  getSalaryApprovalDetailV2,
  pushPayrollApprovalToFinansist,
} from "../../../../api/timesheetApi";
import {
  FINANSIST_PUSH_DRY_RUN,
  buildEmployeesFromApprovalDetail,
  buildUserIdToNameFromApprovalDetail,
  formatFinansistPushResultMessage,
  getFailedUserIdsFromPushResult,
  getLevelName,
} from "../../../../utils/finansistPayrollPush";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import "../../../Approvals/SalaryApproval/SalaryApprovalDetail.css";

const PUSH_MODE_ALL = "all";
const PUSH_MODE_SELECTED = "selected";

const FinansistPushActions = ({
  approvalId,
  calculationYear,
  month,
  visible,
}) => {
  const { userData } = useContext(UserContext) || {};
  const isAdms = (userData?.access_levels || []).some(
    (level) => getLevelName(level) === "АДМС",
  );

  const [isPushingToFinansist, setIsPushingToFinansist] = useState(false);
  const [isCheckingPushed, setIsCheckingPushed] = useState(false);
  const [alreadyPushedToFinansist, setAlreadyPushedToFinansist] =
    useState(false);
  const [showFinansistModal, setShowFinansistModal] = useState(false);
  const [showFinansistResultModal, setShowFinansistResultModal] =
    useState(false);
  const [finansistResultText, setFinansistResultText] = useState("");
  const [finansistResultVariant, setFinansistResultVariant] =
    useState("success");

  const [pushMode, setPushMode] = useState(PUSH_MODE_ALL);
  const [employees, setEmployees] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError] = useState("");
  const [failedUserIds, setFailedUserIds] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState(PUSH_MODE_ALL);
  const [deleteSelectedUserIds, setDeleteSelectedUserIds] = useState([]);
  const [deleteEmployeeSearch, setDeleteEmployeeSearch] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteEmployees, setDeleteEmployees] = useState([]);
  const [isLoadingDeleteEmployees, setIsLoadingDeleteEmployees] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!visible || !approvalId || !calculationYear || !month) {
      setAlreadyPushedToFinansist(false);
      return undefined;
    }

    let cancelled = false;

    const checkAlreadyPushed = async () => {
      setIsCheckingPushed(true);
      try {
        const data = await getPayrollDocumentExternalApprovalIds({
          calculation_year: calculationYear,
          month,
        });
        if (cancelled) return;

        const ids = Array.isArray(data?.approval_ids) ? data.approval_ids : [];
        setAlreadyPushedToFinansist(
          ids.some((id) => Number(id) === Number(approvalId)),
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setAlreadyPushedToFinansist(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingPushed(false);
        }
      }
    };

    checkAlreadyPushed();

    return () => {
      cancelled = true;
    };
  }, [visible, approvalId, calculationYear, month]);

  useEffect(() => {
    if (!showFinansistModal || !approvalId) return undefined;

    let cancelled = false;

    const loadEmployees = async () => {
      setIsLoadingEmployees(true);
      setEmployeesError("");
      try {
        const approvalDetail = await getSalaryApprovalDetailV2(approvalId);
        if (cancelled) return;
        setEmployees(buildEmployeesFromApprovalDetail(approvalDetail));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setEmployees([]);
          setEmployeesError(
            e?.message || "Не удалось загрузить список сотрудников.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEmployees(false);
        }
      }
    };

    loadEmployees();

    return () => {
      cancelled = true;
    };
  }, [showFinansistModal, approvalId]);

  useEffect(() => {
    if (!showDeleteModal || !approvalId) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoadingDeleteEmployees(true);
      setDeleteError("");
      try {
        const detail = await getSalaryApprovalDetailV2(approvalId);
        if (!cancelled) setDeleteEmployees(buildEmployeesFromApprovalDetail(detail));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setDeleteEmployees([]);
          setDeleteError(e?.message || "Не удалось загрузить список сотрудников.");
        }
      } finally {
        if (!cancelled) setIsLoadingDeleteEmployees(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showDeleteModal, approvalId]);

  const filteredDeleteEmployees = useMemo(() => {
    const q = deleteEmployeeSearch.trim().toLowerCase();
    if (!q) return deleteEmployees;
    return deleteEmployees.filter(
      (emp) => emp.name.toLowerCase().includes(q) || String(emp.id).includes(q),
    );
  }, [deleteEmployees, deleteEmployeeSearch]);

  const filteredEmployees = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employees;
    return employees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(query) ||
        String(employee.id).includes(query),
    );
  }, [employees, employeeSearch]);

  if (!visible || !approvalId) {
    return null;
  }

  const resetPushModalState = ({
    mode = PUSH_MODE_ALL,
    userIds = [],
  } = {}) => {
    setPushMode(mode);
    setSelectedUserIds(userIds);
    setEmployeeSearch("");
    setEmployeesError("");
  };

  const openFinansistModal = ({ mode = PUSH_MODE_ALL, userIds = [] } = {}) => {
    resetPushModalState({ mode, userIds });
    setShowFinansistResultModal(false);
    setShowFinansistModal(true);
  };

  const closeFinansistModal = () => {
    setShowFinansistModal(false);
    resetPushModalState();
  };

  const closeFinansistResultModal = () => {
    setShowFinansistResultModal(false);
    setFinansistResultText("");
    setFailedUserIds([]);
  };

  const toggleUserId = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const selectAllFiltered = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      for (const employee of filteredEmployees) {
        next.add(employee.id);
      }
      return Array.from(next);
    });
  };

  const clearSelection = () => {
    setSelectedUserIds([]);
  };

  const handlePushToFinansist = async () => {
    if (pushMode === PUSH_MODE_SELECTED && selectedUserIds.length === 0) {
      setEmployeesError("Выберите хотя бы одного сотрудника.");
      return;
    }

    setIsPushingToFinansist(true);
    setEmployeesError("");
    try {
      const payload = {
        approval_id: approvalId,
        dry_run: FINANSIST_PUSH_DRY_RUN,
        on_missing_contragent: "skip",
        include_documents_in_response: true,
      };
      if (pushMode === PUSH_MODE_SELECTED) {
        payload.user_ids = selectedUserIds;
      }

      const result = await pushPayrollApprovalToFinansist(payload);
      const nextFailedIds = getFailedUserIdsFromPushResult(result);
      const hasPerUserFailures = nextFailedIds.length > 0;
      let userIdToName = new Map();
      if (hasPerUserFailures) {
        if (employees.length > 0) {
          userIdToName = new Map(
            employees.map((employee) => [employee.id, employee.name]),
          );
        } else {
          try {
            const approvalDetail = await getSalaryApprovalDetailV2(approvalId);
            userIdToName = buildUserIdToNameFromApprovalDetail(approvalDetail);
          } catch (e) {
            console.error(e);
          }
        }
      }
      setAlreadyPushedToFinansist(true);
      setFailedUserIds(nextFailedIds);
      setFinansistResultVariant("success");
      setFinansistResultText(
        formatFinansistPushResultMessage(result, userIdToName),
      );
      closeFinansistModal();
      setShowFinansistResultModal(true);
    } catch (e) {
      console.error(e);
      setFailedUserIds([]);
      setFinansistResultVariant("error");
      setFinansistResultText(
        e?.message || "Не удалось отправить зарплату в Finansist.",
      );
      closeFinansistModal();
      setShowFinansistResultModal(true);
    } finally {
      setIsPushingToFinansist(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteMode(PUSH_MODE_ALL);
    setDeleteSelectedUserIds([]);
    setDeleteEmployeeSearch("");
    setDeleteError("");
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteError("");
  };

  const toggleDeleteUserId = (userId) => {
    setDeleteSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleDeleteMappings = async () => {
    const isSelectMode_ = deleteMode === PUSH_MODE_SELECTED;
    if (isSelectMode_ && deleteSelectedUserIds.length === 0) {
      setDeleteError("Выберите хотя бы одного сотрудника.");
      return;
    }

    setIsDeleting(true);
    setDeleteError("");
    try {
      if (isSelectMode_) {
        const results = [];
        for (const uid of deleteSelectedUserIds) {
          const res = await deletePayrollDocumentExternalMappings({
            approval_id: approvalId,
            user_id: uid,
          });
          results.push(res);
        }
        const total = results.reduce((s, r) => s + (r.deleted_count || 0), 0);
        setFinansistResultVariant("success");
        setFinansistResultText(
          `Удалено маппингов: ${total} (по ${deleteSelectedUserIds.length} сотрудникам).`,
        );
      } else {
        const res = await deletePayrollDocumentExternalMappings({
          approval_id: approvalId,
        });
        setFinansistResultVariant("success");
        setFinansistResultText(
          `Удалено маппингов: ${res.deleted_count || 0} (всё согласование #${approvalId}).`,
        );
      }
      setAlreadyPushedToFinansist(false);
      setFailedUserIds([]);
      closeDeleteModal();
      setShowFinansistResultModal(true);
    } catch (e) {
      console.error(e);
      setDeleteError(e?.message || "Не удалось удалить маппинги.");
    } finally {
      setIsDeleting(false);
    }
  };

  const isPushDisabled = isPushingToFinansist || isCheckingPushed;
  const isSelectMode = pushMode === PUSH_MODE_SELECTED;
  const canConfirmPush =
    !isPushingToFinansist &&
    !isLoadingEmployees &&
    (!isSelectMode || selectedUserIds.length > 0);

  const pushButtonLabel = (() => {
    if (isCheckingPushed) {
      return "Проверка Finansist...";
    }
    if (isPushingToFinansist) {
      return "Отправка в Finansist...";
    }
    if (alreadyPushedToFinansist) {
      return "Отправить повторно в Finansist";
    }
    return "Отправить в Finansist";
  })();

  return (
    <>
      {alreadyPushedToFinansist && !isCheckingPushed && (
        <span
          className="salary-detail__finansist-hint"
          title="Данные по этому согласованию уже отправлялись в Finansist"
        >
          Уже отправлялось в Finansist
        </span>
      )}
      <button
        type="button"
        className="salary-detail__button salary-detail__button--submit"
        onClick={() => openFinansistModal()}
        disabled={isPushDisabled}
      >
        {pushButtonLabel}
      </button>

      {isAdms && alreadyPushedToFinansist && !isCheckingPushed && (
        <button
          type="button"
          className="salary-detail__button salary-detail__button--danger"
          onClick={openDeleteModal}
          disabled={isDeleting}
        >
          {isDeleting ? "Удаление маппингов..." : "Удалить маппинги"}
        </button>
      )}

      {showDeleteModal && (
        <div className="popup-overlay" onClick={closeDeleteModal}>
          <div
            className="popup popup-reject popup-finansist-push"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">
              Удаление маппингов Finansist
            </h3>
            <p className="popup-reject__warning">
              Будут удалены локальные маппинги идемпотентности. Документы в
              Finansist API не удаляются.
            </p>

            <div className="popup-finansist-push__modes" role="radiogroup">
              <label className="popup-finansist-push__mode">
                <input
                  type="radio"
                  name="finansist-delete-mode"
                  checked={deleteMode === PUSH_MODE_ALL}
                  onChange={() => setDeleteMode(PUSH_MODE_ALL)}
                  disabled={isDeleting}
                />
                Всё согласование
              </label>
              <label className="popup-finansist-push__mode">
                <input
                  type="radio"
                  name="finansist-delete-mode"
                  checked={deleteMode === PUSH_MODE_SELECTED}
                  onChange={() => setDeleteMode(PUSH_MODE_SELECTED)}
                  disabled={isDeleting}
                />
                По сотрудникам
              </label>
            </div>

            {deleteMode === PUSH_MODE_SELECTED && (
              <div className="popup-finansist-push__picker">
                <div className="popup-finansist-push__picker-toolbar">
                  <input
                    type="search"
                    className="popup-finansist-push__search"
                    placeholder="Поиск по ФИО или id"
                    value={deleteEmployeeSearch}
                    onChange={(e) => setDeleteEmployeeSearch(e.target.value)}
                    disabled={isDeleting || isLoadingDeleteEmployees}
                  />
                  <div className="popup-finansist-push__picker-actions">
                    <button
                      type="button"
                      className="popup-finansist-push__link-btn"
                      onClick={() =>
                        setDeleteSelectedUserIds((prev) => {
                          const next = new Set(prev);
                          for (const emp of filteredDeleteEmployees) next.add(emp.id);
                          return Array.from(next);
                        })
                      }
                      disabled={isDeleting || isLoadingDeleteEmployees || filteredDeleteEmployees.length === 0}
                    >
                      Выбрать всех
                    </button>
                    <button
                      type="button"
                      className="popup-finansist-push__link-btn"
                      onClick={() => setDeleteSelectedUserIds([])}
                      disabled={isDeleting || deleteSelectedUserIds.length === 0}
                    >
                      Сбросить
                    </button>
                  </div>
                </div>
                <div className="popup-finansist-push__picker-meta">
                  Выбрано: {deleteSelectedUserIds.length}
                  {deleteEmployees.length > 0 ? ` из ${deleteEmployees.length}` : ""}
                </div>
                <div className="popup-finansist-push__list">
                  {isLoadingDeleteEmployees && (
                    <div className="popup-finansist-push__empty">Загрузка сотрудников...</div>
                  )}
                  {!isLoadingDeleteEmployees && filteredDeleteEmployees.length === 0 && (
                    <div className="popup-finansist-push__empty">
                      {deleteEmployees.length === 0 ? "Сотрудники не найдены." : "Нет совпадений по поиску."}
                    </div>
                  )}
                  {!isLoadingDeleteEmployees &&
                    filteredDeleteEmployees.map((emp) => (
                      <label key={emp.id} className="popup-finansist-push__item">
                        <input
                          type="checkbox"
                          checked={deleteSelectedUserIds.includes(emp.id)}
                          onChange={() => toggleDeleteUserId(emp.id)}
                          disabled={isDeleting}
                        />
                        <span className="popup-finansist-push__item-name">{emp.name}</span>
                        <span className="popup-finansist-push__item-id">id: {emp.id}</span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {deleteError && (
              <span className="popup-reject__error">{deleteError}</span>
            )}

            <p className="popup-reject__subtitle popup-finansist-push__params">
              <code>approval_id={approvalId}</code>
              {deleteMode === PUSH_MODE_SELECTED && deleteSelectedUserIds.length > 0 && (
                <>, <code>user_ids=[{deleteSelectedUserIds.join(", ")}]</code></>
              )}
            </p>

            <div className="popup-buttons">
              <button
                type="button"
                className="popup-button popup-button-cancel"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="popup-button popup-button-confirm popup-button-confirm--reject"
                onClick={handleDeleteMappings}
                disabled={
                  isDeleting ||
                  isLoadingDeleteEmployees ||
                  (deleteMode === PUSH_MODE_SELECTED && deleteSelectedUserIds.length === 0)
                }
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinansistModal && (
        <div className="popup-overlay" onClick={closeFinansistModal}>
          <div
            className="popup popup-reject popup-finansist-push"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">Отправка в Finansist</h3>
            {alreadyPushedToFinansist && (
              <p className="popup-reject__warning">
                Данные по этому согласованию уже отправлялись в Finansist. Если
                были изменения — можно отправить повторно.
              </p>
            )}
            <p className="popup-reject__subtitle">
              {FINANSIST_PUSH_DRY_RUN
                ? "Тестовый прогон (dry_run): документы в Finansist не создаются."
                : "Будет выполнена фактическая отправка в Finansist."}
            </p>

            <div className="popup-finansist-push__modes" role="radiogroup">
              <label className="popup-finansist-push__mode">
                <input
                  type="radio"
                  name="finansist-push-mode"
                  checked={pushMode === PUSH_MODE_ALL}
                  onChange={() => setPushMode(PUSH_MODE_ALL)}
                  disabled={isPushingToFinansist}
                />
                Все сотрудники
              </label>
              <label className="popup-finansist-push__mode">
                <input
                  type="radio"
                  name="finansist-push-mode"
                  checked={pushMode === PUSH_MODE_SELECTED}
                  onChange={() => setPushMode(PUSH_MODE_SELECTED)}
                  disabled={isPushingToFinansist}
                />
                Выборочно
              </label>
            </div>

            {isSelectMode && (
              <div className="popup-finansist-push__picker">
                <div className="popup-finansist-push__picker-toolbar">
                  <input
                    type="search"
                    className="popup-finansist-push__search"
                    placeholder="Поиск по ФИО или id"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    disabled={isPushingToFinansist || isLoadingEmployees}
                  />
                  <div className="popup-finansist-push__picker-actions">
                    <button
                      type="button"
                      className="popup-finansist-push__link-btn"
                      onClick={selectAllFiltered}
                      disabled={
                        isPushingToFinansist ||
                        isLoadingEmployees ||
                        filteredEmployees.length === 0
                      }
                    >
                      Выбрать всех
                    </button>
                    <button
                      type="button"
                      className="popup-finansist-push__link-btn"
                      onClick={clearSelection}
                      disabled={
                        isPushingToFinansist || selectedUserIds.length === 0
                      }
                    >
                      Сбросить
                    </button>
                  </div>
                </div>
                <div className="popup-finansist-push__picker-meta">
                  Выбрано: {selectedUserIds.length}
                  {employees.length > 0 ? ` из ${employees.length}` : ""}
                </div>
                <div className="popup-finansist-push__list">
                  {isLoadingEmployees && (
                    <div className="popup-finansist-push__empty">
                      Загрузка сотрудников...
                    </div>
                  )}
                  {!isLoadingEmployees && filteredEmployees.length === 0 && (
                    <div className="popup-finansist-push__empty">
                      {employees.length === 0
                        ? "Сотрудники не найдены."
                        : "Нет совпадений по поиску."}
                    </div>
                  )}
                  {!isLoadingEmployees &&
                    filteredEmployees.map((employee) => (
                      <label
                        key={employee.id}
                        className="popup-finansist-push__item"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(employee.id)}
                          onChange={() => toggleUserId(employee.id)}
                          disabled={isPushingToFinansist}
                        />
                        <span className="popup-finansist-push__item-name">
                          {employee.name}
                        </span>
                        <span className="popup-finansist-push__item-id">
                          id: {employee.id}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {employeesError && (
              <span className="popup-reject__error">{employeesError}</span>
            )}

            <p className="popup-reject__subtitle popup-finansist-push__params">
              <code>approval_id={approvalId}</code>,{" "}
              <code>dry_run={FINANSIST_PUSH_DRY_RUN ? "true" : "false"}</code>,{" "}
              <code>on_missing_contragent=skip</code>
              {isSelectMode && selectedUserIds.length > 0 && (
                <>
                  , <code>user_ids=[{selectedUserIds.join(", ")}]</code>
                </>
              )}
            </p>

            <div className="popup-buttons">
              <button
                type="button"
                className="popup-button popup-button-cancel"
                onClick={closeFinansistModal}
                disabled={isPushingToFinansist}
              >
                Отмена
              </button>
              <button
                type="button"
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={handlePushToFinansist}
                disabled={!canConfirmPush}
              >
                {isPushingToFinansist
                  ? "Отправка..."
                  : alreadyPushedToFinansist
                    ? "Отправить повторно"
                    : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinansistResultModal && (
        <div className="popup-overlay" onClick={closeFinansistResultModal}>
          <div
            className="popup popup-finansist-result"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className={`popup-finansist-result__title ${
                finansistResultVariant === "error"
                  ? "popup-finansist-result__title--error"
                  : ""
              }`}
            >
              {finansistResultVariant === "error"
                ? "Ошибка отправки в Finansist"
                : "Результат отправки в Finansist"}
            </h3>
            <pre className="popup-finansist-result__body">
              {finansistResultText}
            </pre>
            <div className="popup-buttons">
              {failedUserIds.length > 0 && (
                <button
                  type="button"
                  className="popup-button popup-button-cancel"
                  onClick={() =>
                    openFinansistModal({
                      mode: PUSH_MODE_SELECTED,
                      userIds: failedUserIds,
                    })
                  }
                  disabled={isPushDisabled}
                >
                  Повторить только с ошибками
                </button>
              )}
              <button
                type="button"
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={closeFinansistResultModal}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FinansistPushActions;
